import { auth, db } from "@/plugins/firebase";
import { collection, addDoc } from "firebase/firestore";
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { isValidEmail } from "@/utils/helper";

export const userAuth = {
  async signUpUser(name, email, password, confirmPassword) {
    if (!name || !email || !password || !confirmPassword) {
      throw new Error("Please fill in all the fields");
    }

    if (!isValidEmail(email)) {
      throw new Error("Please enter a valid email address");
    }

    if (password !== confirmPassword) {
      throw new Error("Passwords do not match");
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      const userData = {
        userId: user.uid,
        name: name,
        email: email,
        credit: 100,
        createdAt: new Date(),
      };

      const docRef = await addDoc(collection(db, "users"), userData);
      console.log("Document written with ID: ", docRef.id);

      return user;
    } catch (error) {
      handleSignUpError(error);
    }
  },
};

function handleSignUpError(error) {
  const errorCode = error.code;

  switch (errorCode) {
    case "auth/email-already-in-use":
      throw new Error("Email is already in use");
    case "auth/invalid-email":
      throw new Error("Invalid email address format");
    case "auth/operation-not-allowed":
      throw new Error("Sign up is currently disabled. Please contact support");
    case "auth/weak-password":
      throw new Error("Password is too weak. Please use a stronger password");
    case "auth/network-request-failed":
      throw new Error(
        "Network error. Please check your connection and try again"
      );
    case "auth/timeout":
      throw new Error("Request timeout. Please try again");
    case "auth/too-many-requests":
      throw new Error("Too many requests. Please try again later");
    case "auth/user-disabled":
      throw new Error("This account has been disabled");
    case "auth/quota-exceeded":
      throw new Error(
        "Service temporarily unavailable. Please try again later"
      );
    case "auth/invalid-credential":
      throw new Error("Invalid signup credentials");
    default:
      console.error("Firebase auth error:", error);
      throw new Error("Sign up failed. Please try again");
  }
}

export const loginService = {
  async loginUser(email, password) {
    if (!email || !password) {
      throw new Error("Please fill in all the fields");
    }

    if (!isValidEmail(email)) {
      throw new Error("Please enter a valid email address");
    }

    try {
      // Set the persistence so the session remains
      await setPersistence(auth, browserLocalPersistence);

      // Perform the sign-in
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      return userCredential.user;
    } catch (error) {
      return loginService.handleLoginError(error);
    }
  },

  handleLoginError(error) {
    const errorCode = error.code;

    switch (errorCode) {
      case "auth/invalid-email":
        throw new Error("Invalid email address format");
      case "auth/user-disabled":
        throw new Error("This account has been disabled");
      case "auth/user-not-found":
        throw new Error("No account found with this email");
      case "auth/wrong-password":
        throw new Error("Incorrect password. Please try again");
      case "auth/too-many-requests":
        throw new Error("Too many failed attempts. Please try again later");
      case "auth/network-request-failed":
        throw new Error("Network error. Please check your connection");
      case "auth/invalid-credential":
        throw new Error("Invalid login credentials");
      default:
        console.error("Firebase auth error:", error);
        throw new Error("Login failed. Please try again");
    }
  },
};
